/**
 * Linking an aircraft of the register to its tracker devices (Teslyn Cloud's Aircraft page; MB's bug of 25 Oct after
 * decision 453; docs/specs/cloud/aircraft-trackers.md). Since decision 453 the cloud poller stores every device a
 * tracking account reports, named or not, so the devices to choose from are those the accounts have reported
 * (`tracker_seen_devices`) plus the tenant's tracker assets. Choosing a device with no asset creates one; the link is
 * the asset's `aircraft_id`. An aircraft may carry several devices (V2Track and TracPlus on VH-JEI): each is an asset
 * with the aircraft's id. A device belongs to one aircraft at a time.
 */
import type { Aircraft } from './model.js';
import type { AircraftKind } from './aircraft-layer.js';
import type { FleetProviderId, TrackerAsset } from './tracker.js';
import { TRACKER_SOURCE_NAME, ageText, normaliseRegistration, suggestTrackerKind } from './tracker.js';
import { normaliseMark } from './aircraft-register.js';

/** A device a tracking account has reported (`tracker_seen_devices`), or a tenant asset no position names. */
export interface SeenDevice {
  readonly provider: FleetProviderId;
  readonly account_label: string;
  readonly device_id: string;
  /** What the tracker calls it: V2Track gives the registration as its name ("VH-JOZ") and no registration. */
  readonly label: string;
  readonly registration: string;
  readonly kind: AircraftKind | null;
  readonly fleet_group: string;
  /** ISO time of its newest stored fix; null for an asset with no stored position. */
  readonly last_seen: string | null;
}

/** One choice of the Aircraft page's tracker list. */
export interface TrackerChoice {
  /** `provider|account|device`: the device key (tracker_asset_device_key). */
  readonly key: string;
  readonly device: SeenDevice;
  /** The tenant's asset of the device, when it has one. */
  readonly asset: TrackerAsset | null;
  /** "VH-JOZ · V2Track (Survey V2Track) · seen 2.0 h ago". */
  readonly text: string;
  /** The device's registration (or its name read as one), upper case; null when it is neither. */
  readonly registration: string | null;
}

export const deviceKey = (d: Pick<SeenDevice, 'provider' | 'account_label' | 'device_id'>): string =>
  `${d.provider}|${d.account_label}|${d.device_id}`;

/** The registration a device reports, else its name when the name is a registration ("VH-JOZ"; not "Ute 2"). */
export function deviceRegistration(d: Pick<SeenDevice, 'registration' | 'label'>): string | null {
  return normaliseRegistration(d.registration) ?? normaliseMark(d.label);
}

/** "VH-JOZ · V2Track (Survey V2Track) · seen 2.0 h ago" (no "seen" for an asset no position names). */
export function trackerChoiceText(d: SeenDevice, asset: TrackerAsset | null, nowMs: number): string {
  const name = asset?.label || d.label || d.registration || d.device_id;
  const provider = TRACKER_SOURCE_NAME[d.provider] + (d.account_label ? ` (${d.account_label})` : '');
  const seen = d.last_seen ? `seen ${ageText(nowMs, Date.parse(d.last_seen))}` : 'not seen in the last 48 h';
  return `${name} · ${provider} · ${seen}`;
}

/**
 * Every device to choose from: the seen devices and the assets (an asset of a seen device is that device), each
 * once, by name.
 */
export function trackerChoices(
  seen: readonly SeenDevice[],
  assets: readonly TrackerAsset[],
  nowMs: number,
): TrackerChoice[] {
  const byKey = new Map<string, { device: SeenDevice; asset: TrackerAsset | null }>();
  for (const d of seen) byKey.set(deviceKey(d), { device: d, asset: null });
  for (const a of assets) {
    if (a.deleted_at) continue;
    const key = deviceKey(a);
    const found = byKey.get(key);
    byKey.set(key, {
      device: found?.device ?? {
        provider: a.provider,
        account_label: a.account_label,
        device_id: a.device_id,
        label: a.label,
        registration: a.registration,
        kind: a.kind,
        fleet_group: a.fleet_group,
        last_seen: null,
      },
      asset: a,
    });
  }
  return [...byKey]
    .map(([key, { device, asset }]) => ({
      key,
      device,
      asset,
      text: trackerChoiceText(device, asset, nowMs),
      registration: deviceRegistration(asset ?? device),
    }))
    .sort((a, b) => a.text.localeCompare(b.text, 'en-AU'));
}

/** The choices linked to an aircraft. */
export function linkedTrackers(choices: readonly TrackerChoice[], aircraftId: string): TrackerChoice[] {
  return choices.filter((c) => c.asset?.aircraft_id === aircraftId);
}

export interface TrackerOption {
  readonly choice: TrackerChoice;
  /** The device's registration is the aircraft's: listed first, "Matches the registration". */
  readonly matches: boolean;
  /** The aircraft the device is linked to now, when another one ("Linked to VH-JEI"). */
  readonly linkedTo: string | null;
}

/**
 * The devices an aircraft can add, those matching its registration first: every device not linked to it already.
 * `aircraft` names the other links.
 */
export function trackerOptionsFor(
  choices: readonly TrackerChoice[],
  target: Pick<Aircraft, 'id' | 'registration'>,
  aircraft: readonly Pick<Aircraft, 'id' | 'registration'>[],
): TrackerOption[] {
  const reg = normaliseRegistration(target.registration);
  return choices
    .filter((c) => c.asset?.aircraft_id !== target.id)
    .map((choice) => {
      const other = choice.asset?.aircraft_id
        ? aircraft.find((a) => a.id === choice.asset?.aircraft_id)
        : null;
      return {
        choice,
        matches: reg !== null && choice.registration === reg,
        linkedTo: other ? other.registration : null,
      };
    })
    .sort(
      (a, b) => Number(b.matches) - Number(a.matches) || a.choice.text.localeCompare(b.choice.text, 'en-AU'),
    );
}

/** A link "Link all matching trackers" would make: an unlinked device whose registration is an aircraft's. */
export interface MatchingLink {
  readonly aircraftId: string;
  readonly registration: string;
  readonly choice: TrackerChoice;
}

export function matchingTrackerLinks(
  choices: readonly TrackerChoice[],
  aircraft: readonly Pick<Aircraft, 'id' | 'registration' | 'deleted_at'>[],
): MatchingLink[] {
  const out: MatchingLink[] = [];
  for (const c of choices) {
    if (!c.registration || c.asset?.aircraft_id) continue;
    const a = aircraft.find((x) => !x.deleted_at && normaliseRegistration(x.registration) === c.registration);
    if (a) out.push({ aircraftId: a.id, registration: a.registration, choice: c });
  }
  return out;
}

/**
 * The tracker asset a device with no asset gets when it is linked: provider, account, device id, name and
 * registration from the device; the kind the device says, else what its name or the aircraft's type suggests.
 */
export function assetForLink(
  d: SeenDevice,
  tenantId: string,
  aircraft: Pick<Aircraft, 'id' | 'registration' | 'airframe' | 'type_name'>,
): Omit<TrackerAsset, 'id'> {
  const kind =
    d.kind ??
    (aircraft.airframe === 'heli'
      ? 'helicopter'
      : aircraft.airframe
        ? 'fixed_wing'
        : (suggestTrackerKind({ label: d.label, fleetGroup: d.fleet_group, typeText: aircraft.type_name }) ??
          'fixed_wing'));
  return {
    tenant_id: tenantId,
    provider: d.provider,
    account_label: d.account_label,
    device_id: d.device_id,
    label: d.label || deviceRegistration(d) || d.device_id,
    registration: deviceRegistration(d) ?? normaliseRegistration(aircraft.registration) ?? '',
    icao_hex: '',
    kind,
    colour: 'accent',
    fleet_group: d.fleet_group,
    aircraft_id: aircraft.id,
    hidden: false,
    deleted_at: null,
  };
}
